import { Component, OnDestroy, OnInit } from "@angular/core";
import { ContextType } from "@charlesschwab/as-ui-core";
import { CoreService, ICurrentContext } from "@charlesschwab/sac-ui-core";
import { select, Store } from "@ngrx/store";
import { Constants } from "projects/money-movement-ui/src/app/shared/utilities/Constants";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { takeUntil } from 'rxjs/operators';
import { AccountInfoActions } from "../../account-info-header/actions";
import { AccountInfo } from "../../account-info-header/models";
import { selectInfo, selectIsTestamentaryTrust, selectIsFsaUser, selectSubAccountId, selectUserContext } from "../../account-info-header/reducers";
import { selectAuthorizeCallFailed, selectAuthorizeCallSuccessful, selectHasAccessInitiateMoneyMovement, selectHasViewMoveMoney } from "../../authorize/reducers";
import { loadCreateStandingAuthEAuthClients, loadEAuthClients } from "../../setup-eAuthorization/actions/eAuthorization.actions";
import { initializePage } from "../../shared/actions/shared.actions";
import { selectCreateTypeInContext, selectViewInContext } from "../../shared/reducers";
import { InitReleaseActions } from "../actions";
import { ValidateWireFlagsService } from "../../shared/services/validate-WireFlags.service";

@Component({
  standalone: false,
    selector: 'mm-initiate-and-release',
    templateUrl: './initiate-and-release.component.html',
    styleUrls: ['./initiate-and-release.component.scss'],
})
export class InitiateAndReleaseComponent implements OnInit, OnDestroy {
    accountInfo: AccountInfo;
    destroy$ = new Subject<void>();
    hasMmInitiateAccess: boolean;
    pageInContext$: Observable<string>;
    typeInContext$: Observable<string>;
    serviceErrorMessage: string = Constants.genericServiceError;
    notSupportedErrorMessage: string = Constants.toAccountValidationError;
    isServiceFailureBannerEnable: boolean = false;
    isTestamentaryTrust: boolean;
    regType: string;
    isFsaUser: boolean;
    userContext: string;
    accountInContext: boolean = false;
    masterAccountInContext: boolean = false;
    hasViewMoneyAccess$: Observable<boolean>;
    authorizedFinished$: Observable<boolean>;
    authorizedFailed$: Observable<boolean>;
    currentContext: ICurrentContext = {
      contextId: 0,
      contextType: ContextType.Unknown,
      firmId: 0,
      firmName: '',
      title: '',
    };
    constructor(private store: Store,
       private coreService: CoreService,
       private validateWireFlagsService: ValidateWireFlagsService) { }

    ngOnInit(): void {
      this.store.dispatch(initializePage());
      this.authorizedFinished$ = this.store.select(selectAuthorizeCallSuccessful);
      this.authorizedFailed$ = this.store.select(selectAuthorizeCallFailed);
      this.authorizedFinished$.pipe(takeUntil(this.destroy$)).subscribe(finished => {
        if (finished){
          this.hasViewMoneyAccess$ = this.store.pipe(select(selectHasViewMoveMoney));
        }
        else {
          this.hasViewMoneyAccess$ = of(false);
        }
      });

      combineLatest([
        this.store.select(selectSubAccountId),
        this.store.select(selectUserContext)
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([sub, context]) => {
        // if master in context, we'll only want to call these once we have a sub acct in context
        // happens upon table action - handled in init release txns table component
        if (sub && context !== ContextType.Master) {
          this.store.dispatch(AccountInfoActions.updateSubAccount({ accountId: sub }));
          this.store.dispatch(InitReleaseActions.refresh({ accountId: sub }));
          this.store.dispatch(loadEAuthClients({ accountId: sub }));
          this.store.dispatch(loadCreateStandingAuthEAuthClients({ accountId: sub }));
        }
    });
        this.store.select(selectInfo)
        .pipe(takeUntil(this.destroy$))
        .subscribe(accountInfo => {
        this.accountInfo = accountInfo;
        this.userContext=accountInfo.userContext;
        });

        this.store.select(selectHasAccessInitiateMoneyMovement)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.hasMmInitiateAccess = response;
        });

      this.store.select(selectIsTestamentaryTrust).pipe(takeUntil(this.destroy$)).subscribe(isTestTrust => {
        this.isTestamentaryTrust = isTestTrust;
      });

      this.store.select(selectIsFsaUser).pipe(takeUntil(this.destroy$)).subscribe(isFsa => {
        this.isFsaUser = isFsa ?? false;
      });

      this.store
        .select(selectInfo)
        .pipe(takeUntil(this.destroy$))
        .subscribe((info) => {
        this.regType = info.registrationType;
        });

      this.pageInContext$ = this.store.select(selectViewInContext);
      this.typeInContext$ = this.store.select(selectCreateTypeInContext);

      this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        if (context) {
          this.currentContext = context;
        }
        if (this.currentContext?.contextType === ContextType.Account 
          || (this.currentContext?.contextType === ContextType.PALAccount 
            && this.validateWireFlagsService.enablePalWireCreateTransaction())) {
          this.accountInContext = true;
          this.masterAccountInContext = false;
        }
        else if (this.currentContext?.contextType === ContextType.Master) {
          this.masterAccountInContext = true;
          this.accountInContext = false;
        }
        else {
          this.masterAccountInContext = false;
          this.accountInContext = false;
        }
      });

      if (this.coreService?.currentContext?.contextType === ContextType.Account 
        || (this.coreService?.currentContext?.contextType === ContextType.PALAccount 
            && this.validateWireFlagsService.enablePalWireCreateTransaction())) {
        this.accountInContext = true;
        this.masterAccountInContext = false;
      }
      else if (this.coreService?.currentContext?.contextType === ContextType.Master) {
        this.masterAccountInContext = true;
        this.accountInContext = false;
      }
      else {
        this.masterAccountInContext = false;
        this.accountInContext = false;
      }
    }

    isServiceFailure(serviceFailureMsg: any): void{
      this.isServiceFailureBannerEnable=serviceFailureMsg;
    }

    get isInitiateAndReleaseFirm(): boolean {
      return !!(this.hasMmInitiateAccess);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }
}
